export type SetupInstructionLanguage = 'en' | 'az' | 'tr';

const TECHNICAL_MARKERS = [
  'conversation.interactive.tools',
  'conversation.sender.tools',
  'helpful AI Assistant embedded in a tenant workspace',
];

export const resolveSetupInstructionLanguage = (language: string | undefined): SetupInstructionLanguage => {
  const normalized = (language ?? 'en').toLowerCase();
  if (normalized.startsWith('az')) {
    return 'az';
  }

  if (normalized.startsWith('tr')) {
    return 'tr';
  }

  return 'en';
};

/** Platform defaults mention kernel tools. Setup should not present those as customer instructions. */
export const isPlatformInstruction = (prompt: string | null | undefined, isDefault = false): boolean => {
  if (isDefault) {
    return true;
  }

  const text = prompt?.trim() ?? '';
  if (!text) {
    return true;
  }

  return TECHNICAL_MARKERS.some(marker => text.includes(marker));
};

export const visibleSetupInstruction = (prompt: string | null | undefined, isDefault = false): string =>
  isPlatformInstruction(prompt, isDefault) ? '' : (prompt ?? '').trim();

export const generateSetupInstructions = (
  businessInfo: string,
  language: string | undefined,
  scope: string,
): string => {
  const info = businessInfo.trim();
  const scopeLine = scope.trim();
  const lang = resolveSetupInstructionLanguage(language);

  if (lang === 'az') {
    return [
      'Müştərilərə onların yazdığı dildə cavab ver.',
      '',
      'Biznes haqqında:',
      info,
      '',
      scopeLine,
      scopeLine ? '' : null,
      'Necə cavab verməli:',
      '- Səmimi, aydın və qısa ol.',
      '- Yalnız yuxarıdakı biznes məlumatına əsaslan.',
      '- Bilmədiyini açıq de və komanda yoldaşı ilə əlaqə təklif et.',
      '- Qiymət, vaxt və qaydaları uydurma.',
    ].filter((line): line is string => line !== null).join('\n');
  }

  if (lang === 'tr') {
    return [
      'Müşterilere kendi yazdıkları dilde yanıt ver.',
      '',
      'İşletme hakkında:',
      info,
      '',
      scopeLine,
      scopeLine ? '' : null,
      'Nasıl yanıt vermeli:',
      '- Sıcak, net ve kısa ol.',
      '- Yalnızca yukarıdaki işletme bilgisine dayan.',
      '- Bilmediğini açıkça söyle ve bir ekip arkadaşına bağlamayı teklif et.',
      '- Fiyat, saat ve kuralları uydurma.',
    ].filter((line): line is string => line !== null).join('\n');
  }

  return [
    'Reply to customers in the language they use.',
    '',
    'About the business:',
    info,
    '',
    scopeLine,
    scopeLine ? '' : null,
    'How to reply:',
    '- Be warm, clear, and brief.',
    '- Answer from the business details above.',
    '- If you do not know something, say so and offer a teammate.',
    '- Do not invent prices, times, or policies.',
  ].filter((line): line is string => line !== null).join('\n');
};
